import { useRef } from 'react';
import { Dialog, Form, Input, Select, MessagePlugin } from 'tdesign-react';
import type { FormInstanceFunctions, FormRule, SubmitContext } from 'tdesign-react';
import {
  SUB_CATEGORY_LABEL,
  SubCategory,
  type WecomChat,
} from '@/services/wecom-chat';

const { FormItem } = Form;

/**
 * 群聊管理-编辑表单值
 *
 * - name:        群聊名称（运维补全；机器人回调拿不到）
 * - subCategory: 绑定的二级分类；选"未绑定"会作为 null 提交解绑
 * - remark:      备注
 *
 * chatid / chatType / 最近消息字段等只展示不可改。
 */
export interface WecomChatFormValue {
  name?: string;
  subCategory?: SubCategory | null;
  remark?: string;
}

interface Props {
  visible: boolean;
  loading?: boolean;
  initialValue?: WecomChat | null;
  onCancel: () => void;
  onSubmit: (payload: WecomChatFormValue) => Promise<void> | void;
}

const SUB_CATEGORY_OPTIONS = [
  { label: '未绑定', value: null as unknown as SubCategory },
  ...Object.entries(SUB_CATEGORY_LABEL).map(([v, label]) => ({
    label,
    value: Number(v) as SubCategory,
  })),
];

const rules: Record<string, FormRule[]> = {
  name: [{ max: 128, message: '群聊名称最多 128 字', type: 'warning' }],
  remark: [{ max: 255, message: '备注最多 255 字', type: 'warning' }],
};

const toFormValue = (record?: WecomChat | null): WecomChatFormValue => ({
  name: record?.name || '',
  subCategory: (record?.subCategory as SubCategory | null | undefined) ?? null,
  remark: record?.remark || '',
});

const normalize = (v: WecomChatFormValue): WecomChatFormValue => ({
  name: v.name?.trim() || '',
  // null 显式传，后端识别为"解绑"
  subCategory: v.subCategory ?? null,
  remark: v.remark?.trim() || '',
});

const WecomChatFormDialog = ({ visible, loading, initialValue, onCancel, onSubmit }: Props) => {
  const formRef = useRef<FormInstanceFunctions>(null);

  const handleSubmit = async (ctx: SubmitContext) => {
    if (ctx.validateResult !== true) return;
    const values = formRef.current?.getFieldsValue?.(true) as WecomChatFormValue | undefined;
    if (!values) return;
    try {
      await onSubmit(normalize(values));
    } catch (err) {
      const msg = err instanceof Error ? err.message : '提交失败';
      MessagePlugin.error(msg);
    }
  };

  const handleConfirm = () => formRef.current?.submit?.();

  return (
    <Dialog
      header="编辑群聊"
      visible={visible}
      onClose={onCancel}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      confirmLoading={loading}
      destroyOnClose
      width={560}
    >
      {/*
        key 绑定到当前编辑群聊 id：每次打开/切换记录，Form 都会以最新 initialData
        重新挂载，确保下拉/输入框初值正确，避免 ref 未挂载时 setFieldsValue 失效。
      */}
      <Form
        key={initialValue?.id ?? 'empty'}
        ref={formRef}
        initialData={toFormValue(initialValue)}
        rules={rules}
        labelWidth={100}
        onSubmit={handleSubmit}
        resetType="initial"
      >
        <FormItem label="ChatID">
          <div style={{ lineHeight: '32px', color: 'var(--td-text-color-secondary)', wordBreak: 'break-all' }}>
            {initialValue?.chatid || '-'}
          </div>
        </FormItem>
        <FormItem label="会话类型">
          <div style={{ lineHeight: '32px', color: 'var(--td-text-color-secondary)' }}>
            {initialValue?.chatType === 'single' ? '单聊' : initialValue?.chatType === 'group' ? '群聊' : (initialValue?.chatType || '-')}
          </div>
        </FormItem>
        <FormItem label="群聊名称" name="name">
          <Input placeholder="请输入群聊名称（用于后台识别）" />
        </FormItem>
        <FormItem label="绑定分类" name="subCategory">
          <Select
            options={SUB_CATEGORY_OPTIONS}
            placeholder="选择该群聊承接的指令分类"
            clearable
          />
        </FormItem>
        <FormItem label="备注" name="remark">
          <Input placeholder="可选备注" />
        </FormItem>
      </Form>
    </Dialog>
  );
};

export default WecomChatFormDialog;
